/**
 * Error Handler Utility
 * Converts FastAPI error responses to readable strings for React display
 * 
 * FastAPI returns errors in two formats:
 * 1. { "detail": "Error message" } - simple string
 * 2. { "detail": [{ "type": "...", "loc": [...], "msg": "...", "input": ... }] } - validation errors array
 * 
 * This utility handles both formats and always returns a string.
 */

/**
 * Extract error message from Axios error or FastAPI response
 * @param {Error|Object} error - The error object from Axios or API response
 * @returns {string} - Human-readable error message
 */
export const getErrorMessage = (error) => {
  // Log the full error for debugging
  console.error('Error details:', error);

  // Handle Axios errors
  if (error?.response?.data) {
    return getErrorMessageFromResponse(error.response.data);
  }

  // Handle network errors
  if (error?.message) {
    if (error.message.includes('Network Error')) {
      return 'Erreur de connexion. Vérifiez votre connexion internet.';
    }
    if (error.message.includes('timeout')) {
      return 'Délai d\'attente dépassé. Veuillez réessayer.';
    }
    return error.message;
  }

  // Handle plain errors
  if (error?.toString) {
    return error.toString();
  }

  // Fallback
  return 'Une erreur inattendue est survenue.';
};

/**
 * Extract error message from FastAPI response data
 * @param {Object} data - The response data from FastAPI
 * @returns {string} - Human-readable error message
 */
const getErrorMessageFromResponse = (data) => {
  // Handle detail field (FastAPI standard)
  if (data.detail) {
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    
    if (Array.isArray(data.detail)) {
      // FastAPI validation errors: array of { type, loc, msg, input }
      const messages = data.detail.map(item => {
        // Extract field name from loc array if available
        let fieldName = '';
        if (Array.isArray(item.loc) && item.loc.length > 0) {
          // loc is like ['body', 'first_name'] or ['query', 'job_offer_id']
          fieldName = item.loc[item.loc.length - 1];
        }
        
        const message = item.msg || item.message || '';
        if (fieldName && message) {
          // Show field name with message for better UX
          return `${fieldName}: ${message}`;
        }
        return message;
      }).filter(msg => msg !== '');
      
      if (messages.length > 0) {
        return messages.join('. ');
      }
      
      // If no msg fields, try to stringify first item
      if (data.detail.length > 0) {
        return JSON.stringify(data.detail[0]);
      }
    }
    
    // If detail is an object but not array
    if (typeof data.detail === 'object' && data.detail !== null) {
      return data.detail.message || data.detail.msg || JSON.stringify(data.detail);
    }
  }

  // Handle other common error fields
  if (data.message) {
    return data.message;
  }
  
  if (data.error) {
    return typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
  }

  // Fallback: stringify the entire response
  try {
    return JSON.stringify(data);
  } catch (e) {
    return 'Une erreur est survenue lors du traitement de la réponse.';
  }
};

/**
 * Extract field-specific error from FastAPI validation errors
 * @param {Error|Object} error - The error object from Axios or API response
 * @param {string} fieldName - The field name to search for in validation errors
 * @returns {string|null} - Error message for the specific field, or null if not found
 */
export const getFieldErrorMessage = (error, fieldName) => {
  if (!error?.response?.data?.detail || !Array.isArray(error.response.data.detail)) {
    return null;
  }

  const fieldError = error.response.data.detail.find(item => {
    if (Array.isArray(item.loc)) {
      return item.loc.includes(fieldName);
    }
    return false;
  });

  return fieldError?.msg || null;
};

export default {
  getErrorMessage,
  getFieldErrorMessage
};
